/** Saved PDF Lab runs, newest first. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PillBadge } from "@/components/ui";
import { BADGE_TONE } from "@/components/ui/pillTones";
import { api } from "@/lib/api";

interface RunSummary {
  runId: string;
  createdAt: string;
  label?: string;
  filename?: string;
  formats?: string[];
  hybrid?: boolean;
  ms?: number;
  userName?: string;
}

export default function PdfLabRuns() {
  const [runs, setRuns] = useState<RunSummary[] | null>(null);

  const load = () => api.get("/api/pdflab/runs").then((d) => setRuns(d.runs));
  useEffect(() => { load(); }, []);

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-fh-text-title">Saved runs</h1>
        <p className="mt-1 text-fh-body text-fh-text-secondary">
          Kept only when you press Save. These expire after{" "}
          <span className="font-medium">30 days</span>, not 90 like OCR Lab's — a PDF Lab run's
          artefacts are too large for the table and live in the uploads bucket, so the row is given
          the same lifetime as the objects it points at rather than outliving them.
        </p>
      </header>

      {!runs && <p className="text-fh-body text-fh-text-muted">Loading…</p>}
      {runs?.length === 0 && (
        <p className="text-fh-body text-fh-text-muted">No saved runs yet.</p>
      )}

      <ul className="space-y-2">
        {runs?.map((r) => (
          <li
            key={r.runId}
            className="flex items-center justify-between rounded-lg bg-white p-4 ring-1 ring-fh-border-soft"
          >
            <Link to={`/pdflab?run=${r.runId}`} className="min-w-0 flex-1">
              <p className="truncate text-fh-body font-medium text-fh-text-strong hover:underline">
                {r.label || r.filename || "(unlabelled)"}
              </p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-fh-meta text-fh-text-muted">
                <span>{new Date(r.createdAt).toLocaleString()}</span>
                {r.filename && r.label && <span>· {r.filename}</span>}
                {r.formats?.length ? <span>· {r.formats.join(", ")}</span> : null}
                {r.ms !== undefined && <span>· {r.ms}ms</span>}
                {r.userName && <span>· {r.userName}</span>}
                {r.hybrid && <PillBadge value="hybrid" tone={BADGE_TONE.purple} />}
              </p>
            </Link>
            <button
              onClick={() => api.del(`/api/pdflab/runs/${r.runId}`).then(load)}
              className="shrink-0 text-fh-meta text-fh-text-muted hover:text-fh-danger"
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
